import type { FC } from 'react'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { useMediaQuery } from 'react-responsive'
import styled, { css, useTheme } from 'styled-components'
import { trackCustomEvent } from 'utils/tracking/custom-events/custom-events'
import { EventAction, EventCategory } from 'utils/tracking/interfaces/data-layer-events'

import { ChevronRight } from './icons'

const Separator = styled<FC<{ className?: string }>>(({ className }) => (
  <i role="separator" className={className}>
    <ChevronRight />
  </i>
))(({ theme }) => ({
  color: 'inherit',
  display: 'flex',
  fill: 'currentColor',
  margin: `0 ${theme.spacings.xxSmall}`,
}))

interface BreadcrumbItemProps {
  key?: string
  titleItem?: boolean
  selected?: boolean
}

const StyledBreadcrumbItem = styled.a<BreadcrumbItemProps>`
  ${({ theme }) => css(theme.variants.body1.regular)};
  color: ${({ theme }) => theme.color.darkGrey700};
  width: auto;
  display: flex;
  align-items: center;

  &:hover {
    cursor: ${({ titleItem }) => !titleItem && 'pointer'};
  }

  @media (min-width: calc(${({ theme }) => theme.breakpoints.tablet} + 1px)) {
    &:hover {
      color: ${({ theme }) => theme.color.fontBlack};
      text-decoration: underline;
    }

    ${({ selected }) =>
      selected &&
      css`
        color: ${({ theme }) => theme.color.fontBlack};
        font-weight: ${({ theme }) => theme.fontWeights.medium};
        text-decoration: none;
        cursor: default;
      `}
  }
`

export interface BreadCrumbItem {
  id: string
  name: string
  slug: string
  href: string
  link: string
}

interface BreadcrumbProps {
  className?: string
  items: BreadCrumbItem[]
  searchParamString?: string
}

interface BreadcrumbLinkProps {
  as: string
  href: string
  id: string
  key?: string
  name: string
  selected?: boolean
  clickHandler: () => void
}

const BreadcrumbLink: FC<BreadcrumbLinkProps> = ({
  href,
  as,
  id,
  name,
  selected = false,
  clickHandler,
}) => {
  return (
    <Link href={href} as={as} key={id} passHref>
      <StyledBreadcrumbItem
        onClick={clickHandler}
        key={id}
        selected={selected}
        data-test-id="product-result-breadcrumb-item"
        data-category-name={name}
      >
        {name}
      </StyledBreadcrumbItem>
    </Link>
  )
}

const createTrackClick = (name: string): void => {
  trackCustomEvent({
    category: EventCategory.NAVIGATION_ELEMENTS,
    action: EventAction.CLICK,
    label: `breadcrumb_${name}`,
  })
}

const _Breadcrumb: FC<BreadcrumbProps> = ({ className, items, searchParamString = '' }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const isTablet = useMediaQuery({ maxWidth: theme.breakpoints.tablet })
  const isTabletDevice = useMediaQuery({ maxDeviceWidth: theme.breakpoints.tablet })
  const tablet = isTablet || isTabletDevice
  const { asPath } = useRouter()

  const topLevelBreadcrumb = (
    <BreadcrumbLink
      href={`/tuotteet${searchParamString}`}
      as={`/tuotteet${searchParamString}`}
      id="topLevelTuotteet"
      name={`${t('Products')}`}
      clickHandler={() => {
        createTrackClick('topLevelTuotteet')
      }}
    />
  )

  if (items.length === 1 && !items[0].id) {
    // This is top level breadcrumb, show title only
    return (
      <nav className={className} data-test-id="product-result-breadcrumb">
        <StyledBreadcrumbItem selected titleItem data-test-id="product-result-breadcrumb-item">
          {t('Products')}
        </StyledBreadcrumbItem>
      </nav>
    )
  }

  if (tablet) {
    if (items.length <= 1) {
      return (
        <nav className={className} data-test-id="product-result-breadcrumb">
          {topLevelBreadcrumb}
        </nav>
      )
    }

    return (
      <nav className={className} data-test-id="product-result-breadcrumb">
        <BreadcrumbLink
          href={`${items[items.length - 2].href}${searchParamString}`}
          as={`${items[items.length - 2].link}${searchParamString}`}
          id={items[items.length - 2].id}
          name={items[items.length - 2].name}
          clickHandler={() => {
            createTrackClick(items[items.length - 2].name)
          }}
        />
      </nav>
    )
  }

  return (
    <nav className={className} data-test-id="product-result-breadcrumb">
      {topLevelBreadcrumb}
      {items.length > 0 && <Separator />}
      {items.map((item, i) => (
        <Fragment key={i}>
          <BreadcrumbLink
            href={`${item.href}${searchParamString}`}
            as={`${item.link}${searchParamString}`}
            key={item.id}
            id={item.id}
            name={item.name}
            /** Do not include query param `?foo=bar` */
            selected={asPath.replace(/\?.*/, '') === item.link}
            clickHandler={() => {
              createTrackClick(item.name)
            }}
          />
          {
            /** Render separator after every item, excluding the last one */
            i < items.length - 1 && <Separator />
          }
        </Fragment>
      ))}
    </nav>
  )
}

export const Breadcrumb = styled(_Breadcrumb)(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  marginBottom: theme.spacings.small,
  marginTop: theme.spacings.small,
}))
